import React, { useState, useEffect, useRef } from 'react';
import { Layout } from '../components/Layout';
import api from '../lib/api';
import { formatDate, formatDateTime } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  MessageSquare,
  Loader2,
  Search,
  User,
  Clock,
  Send,
  Paperclip,
  Download,
  Image,
  File,
  X,
  Eye,
  ChevronRight,
  Mail,
  Phone,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Circle,
  UserCheck,
} from 'lucide-react';
import { toast } from 'sonner';
import { RaiseTicketDialog } from '../components/RaiseTicketDialog';

const PRIORITY_COLORS = {
  low: 'bg-slate-100 text-slate-700',
  medium: 'bg-blue-100 text-blue-700',
  high: 'bg-amber-100 text-amber-700',
  urgent: 'bg-red-100 text-red-700',
};

const STATUS_COLORS = {
  open: 'bg-blue-100 text-blue-700',
  in_progress: 'bg-amber-100 text-amber-700',
  awaiting_customer: 'bg-purple-100 text-purple-700',
  escalated: 'bg-red-100 text-red-700',
  closed: 'bg-emerald-100 text-emerald-700',
};

const STATUS_ICONS = {
  open: Circle,
  in_progress: Clock,
  awaiting_customer: UserCheck,
  escalated: AlertTriangle,
  closed: CheckCircle2,
};

const STATUS_OPTIONS = [
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'awaiting_customer', label: 'Awaiting Customer Reply' },
  { value: 'escalated', label: 'Escalated' },
  { value: 'closed', label: 'Closed' },
];

const API_URL = '';

export const SupportInbox = () => {
  const [tickets, setTickets] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketDialog, setTicketDialog] = useState(false);
  const [raiseTicketOpen, setRaiseTicketOpen] = useState(false);
  const [response, setResponse] = useState('');
  const [updating, setUpdating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchTickets();
    fetchUsers();
  }, [statusFilter, priorityFilter]);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (priorityFilter !== 'all') params.priority = priorityFilter;
      
      const response = await api.get('/tickets', { params });
      setTickets(response.data);
    } catch (error) {
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/admin/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users');
    }
  };

  const fetchTicketDetails = async (ticketId) => {
    try {
      const response = await api.get(`/tickets/${ticketId}`);
      setSelectedTicket(response.data);
    } catch (error) {
      toast.error('Failed to load ticket details');
    }
  };

  const handleOpenTicket = async (ticket) => {
    setSelectedTicket(ticket);
    setResponse('');
    setTicketDialog(true);
    // Fetch fresh ticket data
    await fetchTicketDetails(ticket.id);
  };

  const handleUpdateTicket = async (updates) => {
    if (!selectedTicket) return;
    
    setUpdating(true);
    try {
      const response = await api.put(`/tickets/${selectedTicket.id}`, updates);
      setSelectedTicket(response.data);
      toast.success('Ticket updated');
      fetchTickets();
    } catch (error) {
      toast.error('Failed to update ticket');
    } finally {
      setUpdating(false);
    }
  };

  const handleSendResponse = async () => {
    if (!response.trim()) {
      toast.error('Please enter a response');
      return;
    }

    await handleUpdateTicket({ response });
    setResponse('');
  };

  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !selectedTicket) return;

    setUploading(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        await api.post(`/tickets/${selectedTicket.id}/attachments`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      toast.success('File(s) uploaded successfully');
      await fetchTicketDetails(selectedTicket.id);
    } catch (error) {
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDownloadAttachment = async (attachment) => {
    try {
      const response = await api.get(
        `/tickets/${selectedTicket.id}/attachments/${attachment.id}`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', attachment.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download file');
    }
  };

  const isPreviewable = (fileType) => {
    return fileType?.startsWith('image/') || fileType === 'application/pdf';
  };

  const filteredTickets = tickets.filter(ticket => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        ticket.subject.toLowerCase().includes(query) ||
        ticket.customer_name.toLowerCase().includes(query) ||
        ticket.customer_email.toLowerCase().includes(query)
      );
    }
    return true;
  });

  const openCount = tickets.filter(t => t.status === 'open').length;
  const inProgressCount = tickets.filter(t => t.status === 'in_progress').length;
  const escalatedCount = tickets.filter(t => t.status === 'escalated').length;

  return (
    <Layout>
      <div className="space-y-6" data-testid="support-inbox">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Support Inbox</h1>
            <p className="text-slate-500">Manage customer support tickets</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <Badge className="bg-blue-100 text-blue-700 px-3 py-1">
              <Circle className="w-3 h-3 mr-1 fill-current" />
              {openCount} Open
            </Badge>
            <Badge className="bg-amber-100 text-amber-700 px-3 py-1">
              <Clock className="w-3 h-3 mr-1" />
              {inProgressCount} In Progress
            </Badge>
            {escalatedCount > 0 && (
              <Badge className="bg-red-100 text-red-700 px-3 py-1">
                <AlertTriangle className="w-3 h-3 mr-1" />
                {escalatedCount} Escalated
              </Badge>
            )}
            <Button
              onClick={() => setRaiseTicketOpen(true)}
              data-testid="raise-ticket-btn"
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              Raise Ticket
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="py-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search by subject, customer name, or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="search-tickets"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[150px]" data-testid="status-filter">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  {STATUS_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                <SelectTrigger className="w-[150px]" data-testid="priority-filter">
                  <SelectValue placeholder="Priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Priority</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Tickets Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              </div>
            ) : filteredTickets.length === 0 ? (
              <div className="text-center py-12">
                <MessageSquare className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                <p className="text-slate-500">No tickets found</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Subject</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Priority</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Assigned To</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTickets.map((ticket) => {
                    const StatusIcon = STATUS_ICONS[ticket.status] || Circle;
                    return (
                      <TableRow 
                        key={ticket.id} 
                        className="cursor-pointer hover:bg-slate-50"
                        onClick={() => handleOpenTicket(ticket)}
                        data-testid={`ticket-row-${ticket.id}`}
                      >
                        <TableCell>
                          <p className="font-medium">{ticket.subject}</p>
                          <p className="text-sm text-slate-500 truncate max-w-[200px]">
                            {ticket.description}
                          </p>
                        </TableCell>
                        <TableCell>
                          <p className="font-medium">{ticket.customer_name}</p>
                          <p className="text-sm text-slate-500">{ticket.customer_email}</p>
                        </TableCell>
                        <TableCell>
                          <span className="capitalize">{ticket.category}</span>
                        </TableCell>
                        <TableCell>
                          <Badge className={PRIORITY_COLORS[ticket.priority]}>
                            {ticket.priority}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={`${STATUS_COLORS[ticket.status]} flex items-center gap-1 w-fit`}>
                            <StatusIcon className="w-3 h-3" />
                            {ticket.status.replace('_', ' ')}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {ticket.assigned_to_name || (
                            <span className="text-slate-400">Unassigned</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-slate-500">
                            {formatDate(ticket.created_at)}
                          </span>
                        </TableCell>
                        <TableCell>
                          <ChevronRight className="w-4 h-4 text-slate-400" />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Ticket Detail Dialog */}
        <Dialog open={ticketDialog} onOpenChange={setTicketDialog}>
          <DialogContent className="max-w-4xl max-h-[90vh] p-0 gap-0">
            {selectedTicket && (
              <div className="flex flex-col h-full max-h-[90vh]">
                {/* Header */}
                <div className="px-6 py-4 border-b bg-slate-50">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <h2 className="text-xl font-semibold text-slate-900">{selectedTicket.subject}</h2>
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        <Badge className={PRIORITY_COLORS[selectedTicket.priority]}>
                          {selectedTicket.priority}
                        </Badge>
                        <Badge className={STATUS_COLORS[selectedTicket.status]}>
                          {selectedTicket.status.replace('_', ' ')}
                        </Badge>
                        <span className="text-sm text-slate-500">
                          #{selectedTicket.id.slice(0, 8)}
                        </span>
                      </div>
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => setTicketDialog(false)}>
                      <X className="w-5 h-5" />
                    </Button>
                  </div>
                  {/* Prominent ticket controls — front-and-centre */}
                  <div className="flex flex-wrap items-end gap-3 mt-4" data-testid="ticket-quick-controls">
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">Status</Label>
                      <Select
                        value={selectedTicket.status}
                        onValueChange={(v) => handleUpdateTicket({ status: v })}
                        disabled={updating}
                      >
                        <SelectTrigger className="h-9 w-52 bg-white" data-testid="status-select-header">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUS_OPTIONS.map(opt => (
                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">Assign to</Label>
                      <Select
                        value={selectedTicket.assigned_to || 'unassigned'}
                        onValueChange={(v) => handleUpdateTicket({ assigned_to: v === 'unassigned' ? null : v })}
                        disabled={updating}
                      >
                        <SelectTrigger className="h-9 w-52 bg-white" data-testid="assign-select-header">
                          <SelectValue placeholder="Unassigned" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="unassigned">Unassigned</SelectItem>
                          {users.map((user) => (
                            <SelectItem key={user.id} value={user.id}>{user.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">Priority</Label>
                      <Select
                        value={selectedTicket.priority}
                        onValueChange={(v) => handleUpdateTicket({ priority: v })}
                        disabled={updating}
                      >
                        <SelectTrigger className="h-9 w-36 bg-white" data-testid="priority-select-header">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">Low</SelectItem>
                          <SelectItem value="medium">Medium</SelectItem>
                          <SelectItem value="high">High</SelectItem>
                          <SelectItem value="urgent">Urgent</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {updating && <Loader2 className="w-4 h-4 animate-spin text-slate-400 mb-2.5" />}
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-hidden">
                  <Tabs defaultValue="conversation" className="h-full flex flex-col">
                    <div className="px-6 pt-4">
                      <TabsList>
                        <TabsTrigger value="conversation" data-testid="tab-conversation">
                          Conversation
                        </TabsTrigger>
                        <TabsTrigger value="details" data-testid="tab-details">
                          Details
                        </TabsTrigger>
                        <TabsTrigger value="attachments" data-testid="tab-attachments">
                          Attachments ({selectedTicket.attachments?.length || 0})
                        </TabsTrigger>
                      </TabsList>
                    </div>

                    {/* Conversation Tab */}
                    <TabsContent value="conversation" className="flex-1 px-6 pb-4 overflow-hidden m-0">
                      <ScrollArea className="h-[300px] mt-4 pr-4">
                        {/* Original Message */}
                        <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-100">
                          <div className="flex items-center gap-2 mb-2">
                            <User className="w-4 h-4 text-blue-600" />
                            <span className="font-medium text-blue-900">{selectedTicket.customer_name}</span>
                            <span className="text-xs text-blue-600 ml-auto">
                              {formatDateTime(selectedTicket.created_at)}
                            </span>
                          </div>
                          <p className="text-slate-700 whitespace-pre-wrap">{selectedTicket.description}</p>
                        </div>

                        {/* Responses */}
                        {selectedTicket.responses && selectedTicket.responses.map((resp) => (
                          <div 
                            key={resp.id} 
                            className={`mb-4 p-4 rounded-lg border ${
                              resp.is_customer 
                                ? 'bg-blue-50 border-blue-100 ml-8' 
                                : 'bg-slate-50 border-slate-200 mr-8'
                            }`}
                          >
                            <div className="flex items-center gap-2 mb-2">
                              <User className={`w-4 h-4 ${resp.is_customer ? 'text-blue-600' : 'text-slate-600'}`} />
                              <span className={`font-medium ${resp.is_customer ? 'text-blue-900' : 'text-slate-900'}`}>
                                {resp.user_name}
                              </span>
                              {resp.is_customer && (
                                <Badge variant="outline" className="text-xs">Customer</Badge>
                              )}
                              <span className="text-xs text-slate-500 ml-auto">
                                {formatDateTime(resp.created_at)}
                              </span>
                            </div>
                            <p className="text-slate-700 whitespace-pre-wrap">{resp.message}</p>
                          </div>
                        ))}
                      </ScrollArea>

                      {/* Reply Box */}
                      <div className="mt-4 pt-4 border-t space-y-3">
                        <Textarea
                          value={response}
                          onChange={(e) => setResponse(e.target.value)}
                          placeholder="Type your response to the customer..."
                          rows={3}
                          data-testid="response-input"
                        />
                        <div className="flex items-center justify-between">
                          <div className="flex gap-2">
                            <input
                              type="file"
                              ref={fileInputRef}
                              onChange={handleFileUpload}
                              multiple
                              accept="image/*,.pdf,.doc,.docx"
                              className="hidden"
                              data-testid="attachment-input"
                            />
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => fileInputRef.current?.click()}
                              disabled={uploading}
                            >
                              {uploading ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              ) : (
                                <Paperclip className="w-4 h-4 mr-2" />
                              )}
                              Attach File
                            </Button>
                          </div>
                          <Button 
                            onClick={handleSendResponse} 
                            disabled={updating || !response.trim()}
                            data-testid="send-response-btn"
                          >
                            {updating ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <Send className="w-4 h-4 mr-2" />
                            )}
                            Send Response
                          </Button>
                        </div>
                        <p className="text-xs text-slate-500">
                          Customer will receive an email notification when you respond
                        </p>
                      </div>
                    </TabsContent>

                    {/* Details Tab */}
                    <TabsContent value="details" className="flex-1 px-6 pb-4 overflow-auto m-0">
                      <div className="space-y-6 mt-4">
                        {/* Customer Info */}
                        <div className="p-4 bg-slate-50 rounded-lg">
                          <h3 className="font-medium text-slate-900 mb-3">Customer Information</h3>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="flex items-center gap-2">
                              <User className="w-4 h-4 text-slate-400" />
                              <span className="text-slate-700">{selectedTicket.customer_name}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Mail className="w-4 h-4 text-slate-400" />
                              <a href={`mailto:${selectedTicket.customer_email}`} className="text-blue-600 hover:underline">
                                {selectedTicket.customer_email}
                              </a>
                            </div>
                          </div>
                        </div>

                        {/* Ticket controls now live in the header (front-and-centre). */}

                        {/* Ticket Meta */}
                        <div className="p-4 bg-slate-50 rounded-lg">
                          <h3 className="font-medium text-slate-900 mb-3">Ticket Details</h3>
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <span className="text-slate-500">Category:</span>
                              <span className="ml-2 capitalize">{selectedTicket.category}</span>
                            </div>
                            <div>
                              <span className="text-slate-500">Created:</span>
                              <span className="ml-2">{formatDateTime(selectedTicket.created_at)}</span>
                            </div>
                            <div>
                              <span className="text-slate-500">Last Updated:</span>
                              <span className="ml-2">{formatDateTime(selectedTicket.updated_at)}</span>
                            </div>
                            <div>
                              <span className="text-slate-500">Responses:</span>
                              <span className="ml-2">{selectedTicket.responses?.length || 0}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </TabsContent>

                    {/* Attachments Tab */}
                    <TabsContent value="attachments" className="flex-1 px-6 pb-4 overflow-auto m-0">
                      <div className="mt-4">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="font-medium text-slate-900">Files & Images</h3>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploading}
                          >
                            {uploading ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <Paperclip className="w-4 h-4 mr-2" />
                            )}
                            Upload File
                          </Button>
                        </div>

                        {selectedTicket.attachments && selectedTicket.attachments.length > 0 ? (
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                            {selectedTicket.attachments.map((attachment) => (
                              <div 
                                key={attachment.id}
                                className="border rounded-lg overflow-hidden"
                              >
                                {/* Preview */}
                                <div className="h-32 bg-slate-100 flex items-center justify-center">
                                  {attachment.file_type?.startsWith('image/') ? (
                                    <img 
                                      src={`${API_URL}/api/tickets/${selectedTicket.id}/attachments/${attachment.id}/preview`}
                                      alt={attachment.filename}
                                      className="h-full w-full object-cover"
                                    />
                                  ) : (
                                    <div className="flex flex-col items-center text-slate-400">
                                      <File className="w-8 h-8" />
                                      <span className="text-xs mt-1 uppercase">
                                        {attachment.file_type?.split('/')[1] || 'file'}
                                      </span>
                                    </div>
                                  )}
                                </div>
                                {/* Info */}
                                <div className="p-3">
                                  <p className="text-sm font-medium truncate" title={attachment.filename}>
                                    {attachment.filename}
                                  </p>
                                  <p className="text-xs text-slate-500">
                                    {attachment.uploaded_by} • {formatDate(attachment.uploaded_at)}
                                  </p>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="w-full mt-2"
                                    onClick={() => handleDownloadAttachment(attachment)}
                                  >
                                    <Download className="w-3 h-3 mr-1" />
                                    Download
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                            <Paperclip className="w-12 h-12 mb-4" />
                            <p>No attachments yet</p>
                            <Button
                              variant="outline"
                              className="mt-4"
                              onClick={() => fileInputRef.current?.click()}
                            >
                              Upload your first file
                            </Button>
                          </div>
                        )}
                      </div>
                    </TabsContent>
                  </Tabs>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        <RaiseTicketDialog
          open={raiseTicketOpen}
          onOpenChange={setRaiseTicketOpen}
          onSuccess={fetchTickets}
        />
      </div>
    </Layout>
  );
};
